import { Injectable } from '@angular/core';
import {
	Notification,
	NotificationAction,
	RemoteData,
	appEntities,
} from '@lycoris-nubila/ngx-lycoris';
import { isEmpty, uniq } from 'lodash-es';
import { Observable, from, of } from 'rxjs';
import { catchError, map, mapTo, startWith, tap } from 'rxjs/operators';

import { NotificationDomain } from 'application/common/model/notification-domain';
import { AppSharedRepositoryPort } from 'application/common/port/app-shared-repository.port';
import { MessageConverterPort } from 'application/message/port/message-converter.port';
import { DocumentApplicationPort } from 'document/port/document-application.port';
import { DocumentRepositoryPort } from 'document/port/document-repository.port';
import { IMessage } from 'domain/message/message';
import { MessageOutput } from 'infrastructure/graphql/messaging/message/model/message.output';
import { ModuleApplicationPort } from 'module/port/module-application.port';
import { ModuleRepositoryPort } from 'module/port/module-repository.port';
import { UserApiPort } from 'user/port/user-api.port';
import { UserApplicationPort } from 'user/port/user-application.port';
import { UserRepositoryPort } from 'user/port/user-repository.port';

import { AppPushApiPort } from '../common/port/app-push-api.port';
import { SendMessageCmd } from './command/send-message.cmd';
import { MessageApiPort } from './port/message-api.port';
import { MessageApplicationPort } from './port/message-application.port';
import { MessageRepositoryPort } from './port/message-repository.port';
import { ObserveMessagesQuery } from './query/observe-messages.query';

@Injectable()
export class MessageApplicationAdapter implements MessageApplicationPort {
	constructor(
		private _userApiPort: UserApiPort,
		private _appPushApiPort: AppPushApiPort,
		private _messageApiPort: MessageApiPort,
		private _userRepositoryPort: UserRepositoryPort,
		private _moduleRepositoryPort: ModuleRepositoryPort,
		private _messageRepositoryPort: MessageRepositoryPort,
		private _documentRepositoryPort: DocumentRepositoryPort,
		private _appSharedRepositoryPort: AppSharedRepositoryPort,
		private _userApplicationPort: UserApplicationPort,
		private _moduleApplicationPort: ModuleApplicationPort,
		private _documentApplicationPort: DocumentApplicationPort,
		private _messageConverterPort: MessageConverterPort
	) {
		this._appPushApiPort
			.observeDomainMessage<null>(NotificationDomain.MESSAGE)
			.subscribe((message) => this._handleMessage(message));
	}

	public markMessagesRead(messagesId: string[]): Promise<void> {
		if (isEmpty(messagesId)) {
			return Promise.resolve();
		}

		return this._messageApiPort
			.markMessagesRead(messagesId)
			.then((messages) =>
				this._userApiPort.fetchUserPendingMessages(
					uniq(messages.map((message) => message.authorId))
				)
			)
			.then((pendingMessages) =>
				this._appSharedRepositoryPort.saveUserPendingMessages(pendingMessages)
			);
	}

	public fetchMessages(
		query: ObserveMessagesQuery
	): Observable<string[] | Error> {
		return this._messageApiPort.fetchMessages(query).pipe(
			tap((messages) => this._saveMessages(messages)),
			map((messages) => messages.map((message) => message.id)),
			catchError((e) => of(Error(e)))
		);
	}

	public observeMessages(query: ObserveMessagesQuery): Observable<IMessage[]> {
		return this._messageRepositoryPort.observeMessages(query);
	}

	public sendMessage(cmd: SendMessageCmd): Promise<void> {
		return from(this._messageApiPort.sendMessage(cmd))
			.pipe(
				tap((message) => this._saveMessages([message])),
				mapTo(null)
			)
			.toPromise();
	}

	private _handleMessage<T>(message: Notification<NotificationDomain, T>) {
		switch (message.action) {
			case NotificationAction.ADD:
				this._userApiPort
					.fetchUserPendingMessages()
					.then((pendingMessages) =>
						this._appSharedRepositoryPort.saveUserPendingMessages(pendingMessages)
					);
				this._messageApiPort
					.fetchMessageById(message.entityId)
					.then((msg) => this._saveMessages([msg]));
				break;
			case NotificationAction.UPDATE:
				this._messageApiPort
					.fetchMessageById(message.entityId)
					.then((msg) => this._saveMessages([msg]));
				break;
			case NotificationAction.DELETE:
				break;
		}
	}

	private _saveMessages(messages: MessageOutput[]): void {
		this._messageRepositoryPort.saveMessages(
			messages.map((message) =>
				this._messageConverterPort.asMessageEntity(message)
			)
		);

		this._saveDocuments(messages);

		this._saveModules(messages);

		this._saveUsers(messages);
	}

	private _saveDocuments(messages: MessageOutput[]): Promise<void> {
		const documentsId = uniq(
			messages.map((n) => n.documentId).filter((id) => !!id)
		);

		return this._documentApplicationPort
			.fetchDocuments({ documentsId })
			.pipe(mapTo(void 0))
			.toPromise();
	}

	private _saveModules(messages: MessageOutput[]): Promise<void> {
		const modulesIds = uniq(messages.map((n) => n.moduleId).filter((id) => !!id));

		return this._moduleApplicationPort.saveModules(modulesIds);
	}

	private _saveUsers(messages: MessageOutput[]): Promise<void> {
		const usersId = uniq(
			messages
				.map((n) => [n.authorId, ...n.participantsId])
				.reduce((p, c) => [...p, ...c], [])
				.filter((id) => !!id)
		);

		return this._userApplicationPort.saveUsers(usersId);
	}
}
